import { useState, useEffect } from 'react';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { styled } from 'baseui';
import { Flex } from '../../../components/Flex';

const InputRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  width: '100%'
});

interface CreditCardData {
  cardNumber: string;
  cardHolder: string;
  expiryDate: string;
  cvv: string;
}

/** Optional copy overrides (SAM-633: the public pay page renders this form in EN or ES). */
export interface CreditCardInputLabels {
  cardNumber: string;
  cardHolder: string;
  expiryDate: string;
  cvv: string;
  required: string;
  invalidCardNumber: string;
  invalidExpiry: string;
  expired: string;
  invalidCvv: string;
  invalidName: string;
  brandNotAccepted?: string;
}

interface CreditCardInputProps {
  value?: CreditCardData;
  onChange: (value: CreditCardData) => void;
  disabled?: boolean;
  labels?: CreditCardInputLabels;
  /** Optional brand detection (SAM-1171 pay page): the brand shows inside the number field, and a
   * brand that is not accepted is an error right away. */
  brandOf?: (cardNumber: string) => { label: string; accepted: boolean } | null;
}

interface ValidationErrors {
  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  cvv?: string;
}

export const CreditCardInput = ({ value, onChange, disabled = false, labels, brandOf }: CreditCardInputProps) => {
  const [cardNumber, setCardNumber] = useState(value?.cardNumber || '');
  const [cardHolder, setCardHolder] = useState(value?.cardHolder || '');
  const [expiryDate, setExpiryDate] = useState(value?.expiryDate || '');
  const [cvv, setCvv] = useState(value?.cvv || '');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [numberFocused, setNumberFocused] = useState(false);

  // Format card number with spaces every 4 digits
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // Max 16 digits + 3 spaces
  };

  // SAM-1281: the number is on screen in full only while it is being typed; afterwards only its last four digits.
  const maskCardNumber = (value: string) =>
    value.replace(/\d/g, (digit, index, all) => (all.slice(index + 1).replace(/\D/g, '').length >= 4 ? '•' : digit));

  // Format expiry date as MM/YY
  const formatExpiryDate = (value: string) => {
    // Browser autofill (autocomplete="cc-exp") may hand over "MM/YYYY": keep the last two digits.
    const full = value.match(/^\s*(\d{1,2})\s*\/\s*(\d{4})\s*$/);
    if (full) return `${full[1].padStart(2, '0')}/${full[2].slice(2)}`;
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  const brand = brandOf ? brandOf(cardNumber) : null;
  const brandRefused = brand?.accepted === false;
  const brandError = labels?.brandNotAccepted ?? 'This card brand is not accepted';

  // Validate card number
  const validateCardNumber = (value: string): string | undefined => {
    const cleaned = value.replace(/\s/g, '');
    if (!cleaned) return labels?.required ?? 'Card number is required';
    if (brandOf?.(cleaned)?.accepted === false) return brandError;
    if (cleaned.length !== 16) return labels?.invalidCardNumber ?? 'Card number must be 16 digits';
    if (!/^\d+$/.test(cleaned)) return labels?.invalidCardNumber ?? 'Card number must contain only digits';
    return undefined;
  };

  // Validate card holder
  const validateCardHolder = (value: string): string | undefined => {
    if (!value) return labels?.required ?? 'Card holder name is required';
    if (value.length < 3) return labels?.invalidName ?? 'Card holder name must be at least 3 characters';
    // Letters in any alphabet (so JOSÉ PEÑA is valid), plus spaces, apostrophes, hyphens and dots.
    if (!/^[\p{L}\s'.-]+$/u.test(value)) return labels?.invalidName ?? 'Card holder name must contain only letters';
    return undefined;
  };

  // Validate expiry date
  const validateExpiryDate = (value: string): string | undefined => {
    if (!value) return labels?.required ?? 'Expiry date is required';
    if (!/^\d{2}\/\d{2}$/.test(value)) return labels?.invalidExpiry ?? 'Expiry date must be in MM/YY format';
    
    const [month, year] = value.split('/').map(Number);
    if (month < 1 || month > 12) return labels?.invalidExpiry ?? 'Invalid month';
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;
    
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return labels?.expired ?? 'Card has expired';
    }
    
    return undefined;
  };

  // Validate CVV
  const validateCvv = (value: string): string | undefined => {
    if (!value) return labels?.required ?? 'CVV is required';
    if (!/^\d{3,4}$/.test(value)) return labels?.invalidCvv ?? 'CVV must be 3 or 4 digits';
    return undefined;
  };

  // Validate all fields
  const validateAll = () => {
    const newErrors: ValidationErrors = {
      cardNumber: validateCardNumber(cardNumber),
      cardHolder: validateCardHolder(cardHolder),
      expiryDate: validateExpiryDate(expiryDate),
      cvv: validateCvv(cvv)
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== undefined);
  };

  // Update parent component when values change
  useEffect(() => {
    onChange({
      cardNumber: cardNumber.replace(/\s/g, ''),
      cardHolder,
      expiryDate,
      cvv
    });
  }, [cardNumber, cardHolder, expiryDate, cvv]);

  // Validate on blur
  const handleBlur = (field: keyof ValidationErrors) => {
    setTouched({ ...touched, [field]: true });
    
    const validators = {
      cardNumber: validateCardNumber,
      cardHolder: validateCardHolder,
      expiryDate: validateExpiryDate,
      cvv: validateCvv
    };
    
    const value = { cardNumber, cardHolder, expiryDate, cvv }[field];
    const error = validators[field](value);
    setErrors({ ...errors, [field]: error });
  };

  return (
    <Flex flexDirection="column" gap="12px" width="100%">
      <FormControl
        label={labels?.cardNumber ?? "Card Number"}
        error={brandRefused ? brandError : touched.cardNumber && errors.cardNumber}
      >
        <Input
          value={numberFocused ? cardNumber : maskCardNumber(cardNumber)}
          onChange={(e) => setCardNumber(formatCardNumber(e.currentTarget.value))}
          onFocus={() => setNumberFocused(true)}
          onBlur={() => { setNumberFocused(false); handleBlur('cardNumber'); }}
          placeholder="1234 5678 9012 3456"
          name="cc-number"
          autoComplete="cc-number"
          disabled={disabled}
          error={brandRefused || (touched.cardNumber && !!errors.cardNumber)}
          type="text"
          inputMode="numeric"
          endEnhancer={brand ? (
            <span data-testid="card-brand" data-accepted={brand.accepted} style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {brand.label}
            </span>
          ) : undefined}
        />
      </FormControl>

      <FormControl
        label={labels?.cardHolder ?? "Card Holder Name"}
        error={touched.cardHolder && errors.cardHolder}
      >
        <Input
          value={cardHolder}
          onChange={(e) => setCardHolder(e.currentTarget.value.toUpperCase())}
          onBlur={() => handleBlur('cardHolder')}
          placeholder="JOHN DOE"
          name="cc-name"
          autoComplete="cc-name"
          disabled={disabled}
          error={touched.cardHolder && !!errors.cardHolder}
          type="text"
        />
      </FormControl>

      <InputRow>
        {/* Each FormControl renders its label and control as siblings: wrap it so the grid gets one cell per field. */}
        <div>
        <FormControl
          label={labels?.expiryDate ?? "Expiry Date"}
          error={touched.expiryDate && errors.expiryDate}
        >
          <Input
            value={expiryDate}
            onChange={(e) => setExpiryDate(formatExpiryDate(e.currentTarget.value))}
            onBlur={() => handleBlur('expiryDate')}
            placeholder="MM/YY"
            name="cc-exp"
            autoComplete="cc-exp"
            disabled={disabled}
            error={touched.expiryDate && !!errors.expiryDate}
            type="text"
            inputMode="numeric"
          />
        </FormControl>
        </div>

        <div>
        <FormControl
          label={labels?.cvv ?? "CVV"}
          error={touched.cvv && errors.cvv}
        >
          <Input
            value={cvv}
            onChange={(e) => {
              const value = e.currentTarget.value.replace(/\D/g, '');
              setCvv(value.substring(0, 4));
            }}
            onBlur={() => handleBlur('cvv')}
            placeholder="123"
            name="cc-csc"
            autoComplete="cc-csc"
            disabled={disabled}
            error={touched.cvv && !!errors.cvv}
            type="password"
            inputMode="numeric"
          />
        </FormControl>
        </div>
      </InputRow>
    </Flex>
  );
};
